import { useEffect, useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useCustomerAuth } from '@/contexts/CustomerAuthContext';

/**
 * Returns the total number of unread chat messages across all conversations.
 * Subscribes to realtime updates.
 */
export function useUnreadChatCount() {
  const { profile } = useCustomerAuth();
  const clientId = profile?.id;
  const [count, setCount] = useState(0);

  useEffect(() => {
    if (!clientId) { setCount(0); return; }

    const fetch = async () => {
      // Get all conversations for this user
      const { data: convos } = await (supabase as any)
        .from('chat_conversations')
        .select('id')
        .or(`participant_a.eq.${clientId},participant_b.eq.${clientId}`);

      if (!convos || convos.length === 0) { setCount(0); return; }

      const ids = convos.map((c: any) => c.id);

      const { count: total } = await (supabase as any)
        .from('chat_messages')
        .select('id', { count: 'exact', head: true })
        .in('conversation_id', ids)
        .neq('sender_id', clientId)
        .is('read_at', null);

      setCount(total || 0);
    };

    fetch();

    // Realtime: listen for new messages
    const channel = supabase
      .channel('unread-chat-count')
      .on('postgres_changes', {
        event: '*',
        schema: 'public',
        table: 'chat_messages',
      }, () => {
        // Re-fetch on any message change
        fetch();
      })
      .subscribe();

    return () => { supabase.removeChannel(channel); };
  }, [clientId]);

  return count;
}
